import type { Suggestion } from "./types";
import { suggestionDims } from "./event-dims";
import type { EventDims } from "./event-dims";

// ── Bet-placement attribution (Digitain) ──────────────────────────────────────
//
// Live-proven 2026-07-08 with a real bet on Hannibalbet: when the user places a
// bet, the Digitain sportsbook iframe posts `{command:"successBet",
// sender:"sportdigi", args:null}` to the parent window. That pulse is the only
// client-side placement signal we get — it carries NO bet id/amount/legs, so
// attribution is temporal: a placement within ATTRIBUTION_WINDOW_MS of our
// successful betslip add(s) is attributed to those adds (observed add→place
// delta in the live test: ~145s). If the user edited the slip in between we may
// over-attribute — the honest limit of the client-side model; the operator's
// bet ledger (CreditBet) is the bulletproof upgrade.
//
// Unattributed pulses are ALSO reported (adds: []) — the organic-bets baseline
// that makes the attributed share meaningful.

/** A successful widget add awaiting a placement pulse. */
export interface PendingAdd {
  suggestion_id: string;
  event_id?: string;
  rec_type?: string;
  selection?: string | null;
  odds?: number | null;
  legs?: unknown[];
  combined_odds?: number;
  dims?: EventDims; // descriptive dims of the placed pick (for tuning breakdowns)
  ts: number;
}

export interface BetPlacedAttribution {
  attributed: boolean;
  /** Seconds from the most recent in-window add to the placement pulse. */
  seconds_since_add: number | null;
  adds: PendingAdd[];
}

/** How long after an add a placement is still credited to it. The live test
 * placed at ~145s; 10 minutes covers a user browsing before confirming without
 * swallowing clearly unrelated bets. */
export const BET_ATTRIBUTION_WINDOW_MS = 10 * 60_000;

// The live capture saw every iframe message delivered twice (listener/world
// duplication) — collapse pulses that arrive within this span into one bet.
const PULSE_DEDUP_MS = 3_000;

export class BetPlacedWatcher {
  private pending: PendingAdd[] = [];
  private lastPulseTs = 0;
  private listener: ((e: MessageEvent) => void) | null = null;

  constructor(
    private onPlaced: (a: BetPlacedAttribution) => void,
    private windowMs: number = BET_ATTRIBUTION_WINDOW_MS,
  ) {}

  /** Call after a suggestion actually landed in the betslip (addStakes ok).
   * `rank` is the card's list position, folded into the dims for tuning. */
  recordAdd(s: Suggestion, rank = 0): void {
    this.pending.push({
      suggestion_id: s.id,
      event_id: s.event_id,
      rec_type: s.rec_type,
      selection: s.selection,
      odds: s.odds,
      legs: s.legs,
      combined_odds: s.combined_odds,
      dims: suggestionDims(s, rank),
      ts: Date.now(),
    });
    if (this.pending.length > 20) this.pending.shift();
  }

  start(): void {
    if (this.listener) return;
    this.listener = (e: MessageEvent): void => this.handleMessage(e.data);
    window.addEventListener("message", this.listener);
  }

  stop(): void {
    if (this.listener) {
      window.removeEventListener("message", this.listener);
      this.listener = null;
    }
  }

  /** Exposed for tests; start() routes real window messages here. */
  handleMessage(data: unknown): void {
    const d = data as { command?: string; sender?: string } | null;
    if (!d || typeof d !== "object" || d.command !== "successBet" || d.sender !== "sportdigi") return;

    const now = Date.now();
    if (now - this.lastPulseTs < PULSE_DEDUP_MS) return; // duplicated delivery
    this.lastPulseTs = now;

    const fresh = this.pending.filter((p) => now - p.ts <= this.windowMs);
    // A placement consumes the slip — pending adds don't carry to the next bet.
    this.pending = [];

    this.onPlaced({
      attributed: fresh.length > 0,
      seconds_since_add: fresh.length
        ? Math.round((now - Math.max(...fresh.map((p) => p.ts))) / 1000)
        : null,
      adds: fresh,
    });
  }
}
